import { isEvoWrappedSelector } from '@lobby/oss/selectors';
import { isAuthParamsErrorSelector } from '@lobby/store/selectors/app';
import {
  cashierConfigSelector,
  definitionsSelector,
  gameTypesSelector,
  generalConfigSelector,
  homeConfigSelector,
  languageCodeSelector,
} from '@lobby/store/selectors/config';
import { externalGameTypesSelector } from '@lobby/store/selectors/externalGameTypes';
import { localeSelector } from '@lobby/store/selectors/locale';
import {
  reserveSeatSelector,
  tablesSelector,
} from '@lobby/store/selectors/tables';
import {
  userSelector, authParamsSelector, userOperatorIdSelector, userTokenSelector, userIdSelector,
} from '@lobby/store/selectors/user';
import {
  openCashier,
  goHome,
  openTable,
  getGameNameById,
} from '@lobby/utils/navigation';
import {
  sendCashierMessage,
  sendHomeMessage,
  sendOpenTableMessage,
} from '@lobby/utils/postMessage';
import { combineEpics, ofType } from 'redux-observable';
import { of, timer } from 'rxjs';
import {
  filter,
  ignoreElements,
  pluck,
  switchMap,
  tap,
  delayWhen,
} from 'rxjs/operators';

import { NAVIGATION_METHODS } from '@ezugi/constants/navigationMethods';
import { getAuthEndpointForExternalIntegration } from '@ezugi/utils/helpers/getAuthEndpointForExternalIntegration';
import ss from '@ezugi/utils/helpers/sessionstorage';

import { navigation } from '../../navigation';
import { socket } from '../../socket';

const cashierEpic = (action$, state$) => action$.pipe(
  ofType(navigation.openCashier),
  tap(() => {
    const { uid } = userSelector(state$.value);
    const authParams = authParamsSelector(state$.value);
    const cashierConfig = cashierConfigSelector(state$.value);
    const isEvoWrapped = isEvoWrappedSelector(state$.value);
    const shouldRedirectBeHandledByParentWindow = authParams.postMessage === '1'
    || isEvoWrapped
    || cashierConfig?.cashier_method === NAVIGATION_METHODS.POST;

    if (shouldRedirectBeHandledByParentWindow) {
      sendCashierMessage(uid);
    } else {
      openCashier(cashierConfig, authParams.cashierUrl);
    }
  }),
  ignoreElements(),
);

const goHomeEpic = (action$, state$) => action$.pipe(
  ofType(navigation.goHome),
  tap(({ payload = {} }) => {
    let authParams = authParamsSelector(state$.value);
    let homeConfig = homeConfigSelector(state$.value);
    const isEvoWrapped = isEvoWrappedSelector(state$.value);
    const shouldRedirectBeHandledByParentWindow = authParams?.postMessage === '1'
    || isEvoWrapped
    || homeConfig?.home_method === NAVIGATION_METHODS.POST;

    if (shouldRedirectBeHandledByParentWindow) {
      sendHomeMessage();
    } else {
      if (isAuthParamsErrorSelector(state$.value) && ss.has('home_url')[1]) {
        authParams = { ...authParams, homeUrl: ss.get('home_url')[1] };
        homeConfig = { ...homeConfig, homeMethod: NAVIGATION_METHODS.REPLACE };
      }
      goHome(
        homeConfig,
        Boolean(payload.outerLink),
        authParams,
        authParams?.identifier
            || userSelector(state$.value).identifier,
      );
    }
  }),
  ignoreElements(),
);

const reserveSeatEpic = (action$, state$) => action$.pipe(
  ofType(navigation.openTable),
  pluck('payload'),
  filter((payload) => payload.seatIndex),
  switchMap((payload) => {
    const { tableId, seatIndex, gameId } = payload;

    const reserveSeatMsg = reserveSeatSelector(state$.value);
    const user = userSelector(state$.value);
    const clientId = `${user.operatorId}|${user.uid}|${tableId}`;

    return of(
      socket.send({
        ClientId: clientId,
        SeatId: seatIndex,
        gameType: gameId,
        TableId: tableId.toString(),
        ...reserveSeatMsg,
      }),
    );
  }),
);

const openTableEpic = (action$, state$) => action$.pipe(
  ofType(navigation.openTable),
  pluck('payload'),
  // Delay navigation for reserve seat message
  delayWhen((payload) => (payload.seatIndex ? timer(100) : of(undefined)),),
  tap((payload) => {
    let { open_table_method: openMethod } = generalConfigSelector(
      state$.value,
    );
    const authParams = authParamsSelector(state$.value);
    const { identifier } = userSelector(state$.value);
    const { SITE_BASE } = definitionsSelector(state$.value);
    const definitions = definitionsSelector(state$.value);
    const gameTypes = gameTypesSelector(state$.value);
    const isEvoWrapped = isEvoWrappedSelector(state$.value);

    if (authParams?.postMessage === '1' || isEvoWrapped) openMethod = NAVIGATION_METHODS.POST;
    const { tableId, gameId, limitId, seatIndex } = payload;
    const locale = localeSelector(state$.value);
    const table = tablesSelector(state$.value)[tableId];
    const tableLocaleTitle = locale?.tableNames
        && locale?.tableNames[`tableId_${tableId}`];
    const tableTitle = table?.title;
    const sanitizedSeatIndex = typeof seatIndex !== 'undefined'
      ? String(seatIndex).replace('s', '')
      : seatIndex;
    const tableName = tableLocaleTitle || tableTitle;
    const gameName = getGameNameById(gameTypes, gameId);
    const pathSegment = getAuthEndpointForExternalIntegration(
      gameId,
      externalGameTypesSelector(state$.value)
    );
    const isExternalIntegration = !!pathSegment;
    const languageCode = languageCodeSelector(state$.value);
    const token = userTokenSelector(state$.value);
    const uid = userIdSelector(state$.value);
    const withLinkPrefix = !!definitions?.LINK_PREFIX;
    const baseURL = withLinkPrefix ? `${SITE_BASE}${definitions.LINK_PREFIX}` : SITE_BASE;
    if (openMethod === NAVIGATION_METHODS.POST) {
      sendOpenTableMessage(
        gameName,
        gameId,
        tableId,
        limitId,
        sanitizedSeatIndex,
        tableName,
        languageCode
      );
    } else {
      ss.put(NAVIGATION_METHODS.LOBBY, '1');
      openTable({
        queryParams: {
          game_id: gameId,
          table_id: tableId,
          limit_id: limitId,
          identifier,
          operatorId: userOperatorIdSelector(state$.value),
          token,
          ...(sanitizedSeatIndex && { seatIndex: sanitizedSeatIndex }),
          ...(!!pathSegment && {
            language: languageCode,
            token,
            uid,
            openTable: tableId,
            fromEzugiLobby: true,
          }),
        },
        method: openMethod,
        outerLink: isExternalIntegration,
        baseURL,
        gameName,
        pathSegment,
      });
    }
  }),
  ignoreElements(),
);

export default combineEpics(
  goHomeEpic,
  cashierEpic,
  reserveSeatEpic,
  openTableEpic,
);
